import type { IntlShape } from 'react-intl';

import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';

import type { ReviewResults } from '../../../pm-plugins/thinking-plugin';

export function getReviewCompleteMessage(
	formatMessage: IntlShape['formatMessage'],
	reviewResults: ReviewResults,
	reviewer?: string,
): string {
	const { count, hadExistingSuggestions } = reviewResults;
	if (count === 0) {
		return formatMessage(aiSuggestionsMessages.thinkingCompleteNoSuggestionsUpdated);
	}
	const message = reviewer
		? hadExistingSuggestions
			? aiSuggestionsMessages.thinkingCompleteNewSuggestionCount
			: aiSuggestionsMessages.thinkingCompleteSuggestionCount
		: hadExistingSuggestions
			? aiSuggestionsMessages.thinkingCompleteNewSuggestionCountWithoutReviewer
			: aiSuggestionsMessages.thinkingCompleteSuggestionCountWithoutReviewer;
	return formatMessage(message, { count, reviewer });
}

/**
 * Returns the domain-specific rotating messages shown while the AI
 * suggestions thinking bar is active.
 *
 * The first status ("Reviewing content") is overridden to name the reviewing
 * agent — see `getReviewingContentWithAgentMessage` — once it resolves.
 */
export function getThinkingRotatingMessages(formatMessage: IntlShape['formatMessage']): string[] {
	return [
		formatMessage(aiSuggestionsMessages.thinkingReviewingContent),
		formatMessage(aiSuggestionsMessages.thinkingMayTakeAFewMinutes),
		formatMessage(aiSuggestionsMessages.thinkingAnalyzingStructure),
		formatMessage(aiSuggestionsMessages.thinkingCheckingRelatedSources),
		formatMessage(aiSuggestionsMessages.thinkingGatheringContext),
		formatMessage(aiSuggestionsMessages.thinkingIdentifyingImprovements),
		formatMessage(aiSuggestionsMessages.thinkingEvaluatingOptions),
		formatMessage(aiSuggestionsMessages.thinkingCheckingConsistency),
		formatMessage(aiSuggestionsMessages.thinkingPreparingSuggestions),
		formatMessage(aiSuggestionsMessages.thinkingRefiningSuggestions),
		formatMessage(aiSuggestionsMessages.thinkingFinalizingSuggestionsForReview),
	];
}

/**
 * The first-status text naming the reviewing agent, e.g. "Reviewing content with
 * {agentName}". Shown (with the agent's avatar) in place of the generic
 * "Reviewing content" once the agent resolves.
 */
export function getReviewingContentWithAgentMessage(
	formatMessage: IntlShape['formatMessage'],
	agentName: string,
): string {
	return formatMessage(aiSuggestionsMessages.thinkingReviewingContentWithAgent, { agentName });
}
